
import {
  Component,
  Provide,
  Prop,
  Inject,
  Mixins
} from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ProvisionRequestStatusCodes as PRSC } from "@/data/enums/contractProducts";

// Types
import {
  IBlueprintField,
  IProvisionConfigurationFieldValue,
  IProvisionConfigurationFunction,
  IProvisionExecutedFunction,
  IProvisionRequest
} from "@/models/provisioning";
import {
  ICProdProviderMethods,
  ICProdProvider
} from "@/models/providers/contractProduct";
import {
  IContract,
  IContractProduct,
  ContractStatusCodes
} from "@/models/contracts";
import {
  ICProdProvProvider,
  ICProdProvProviderMethods,
  IProvisioningFunctionSetup
} from "@/models/providers/contractProduct";
import { IProvider } from "@/models/providers";
import { ISocketMessage } from "@/models/socket";
import { OrgConfigKeys } from "@/data/constants";

@Component({
  computed: {
    ...mapState({
      contractProductFieldsScope(state, getters) {
        return getters[
          `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/contractProductFieldsScope`
        ](this.contractId, this.productId);
      },
      contractProductFields(state, getters) {
        return (
          getters[`data/listArray`]({
            storeModule: DataModules.PROVISIONING_CONTRACT_PRODUCTS,
            scope: this.contractProductFieldsScope
          }) || []
        );
      },
      contractProductFieldsValuesScope(state, getters) {
        return getters[
          `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/contractProductFieldsValuesScope`
        ](this.contractId, this.productId);
      },
      contractProductFieldsValues(state, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PROVISIONING_CONTRACT_PRODUCTS,
          scope: this.contractProductFieldsValuesScope
        });
      },
      contractProductFunctionsScope(state, getters) {
        return getters[
          `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/contractProductFunctionsScope`
        ](this.contractId, this.productId);
      },
      contractProductFunctions(state, getters) {
        return (
          getters[`data/listArray`]({
            storeModule: DataModules.PROVISIONING_CONTRACT_PRODUCTS,
            scope: this.contractProductFunctionsScope
          }) || []
        );
      },
      contractProductProvisionRequestsScope(state, getters) {
        return getters[
          `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/contractProductProvisionRequestsScope`
        ](this.contractId, this.productId);
      },
      contractProductProvisionRequests(state, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PROVISIONING_CONTRACT_PRODUCTS,
          scope: this.contractProductProvisionRequestsScope
        });
      }
    })
  }
})
export default class CProdProvProvider extends Mixins(RequestPromises, Context)
  implements IProvider {
  // Inject

  @Inject({ from: "contractProductProviderMethods" })
  public readonly $cProdMethods!: ICProdProviderMethods;

  @Inject({ from: "contractProductProvider" })
  public readonly $cProdData!: () => ICProdProvider;

  public get cProdData() {
    return this.$cProdData();
  }

  // Props

  @Prop({ required: true })
  public productId!: IContractProduct["id"];

  @Prop({ required: true })
  public contractId!: IContract["id"];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Data

  public contractProductFields!: IBlueprintField[];
  public contractProductFieldsScope!: string;
  public contractProductFieldsValues!: IProvisionConfigurationFieldValue[];
  public contractProductFieldsValuesScope!: string;
  public contractProductFunctions!: IProvisionConfigurationFunction[];
  public contractProductFunctionsScope!: string;
  public contractProductProvisionRequests!: IProvisionRequest[];
  public contractProductProvisionRequestsScope!: string;
  public hasRequestError = false;
  public isLoading = true;
  public isProcessing = false;
  public isReloading = false;

  // Life-cycle methods

  public created() {
    this.setAsyncFunctionExecutedEvent();
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_CONTRACT_PRODUCTS,
      scope: this.contractProductFieldsScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_CONTRACT_PRODUCTS,
      scope: this.contractProductFieldsValuesScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_CONTRACT_PRODUCTS,
      scope: this.contractProductFunctionsScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_CONTRACT_PRODUCTS,
      scope: this.contractProductProvisionRequestsScope,
      vm: this
    });

    this.$bus.$off("socket-provision-result-received");
  }

  // Computed
  public get hasProvisioning() {
    return this.$store.getters["org/hasProvisioningEnabled"];
  }

  public get canManageProvisioning() {
    return (
      this.hasProvisioning &&
      (this.contractProductFields.length > 0 ||
        this.contractProductFunctions.length > 0)
    );
  }

  // Methods

  public async loadData() {
    try {
      await this.$store.dispatch("constants/getStatuses", "provision_request");
      await this.getOrgProvisioning(this.isReloading);
      if (this.hasProvisioning) {
        if (!this.isAdmin) {
          this.runClientAreaProvisionFunctions();
        }

        await Promise.all([
          this.getContractProductFields(this.isReloading),
          this.getContractProductFieldsValues(this.isReloading),
          this.getContractProductFunctions(this.isReloading)
        ]);
      }

      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getOrgProvisioning(ignoreStored = false) {
    return this.$store.dispatch("org/getConfig", {
      ignoreStored,
      keys: [OrgConfigKeys.PRODUCT_PROVISIONING_ENABLED]
    });
  }

  public getContractProductFields(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/listContractProductFields`,
      {
        contractId: this.contractId,
        productId: this.productId,
        scope: this.contractProductFieldsScope,
        vm: this,
        ignoreStored
      }
    );
  }

  public getContractProductFieldsValues(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/listContractProductFieldsValues`,
      {
        contractId: this.contractId,
        productId: this.productId,
        scope: this.contractProductFieldsValuesScope,
        vm: this,
        ignoreStored
      }
    );
  }

  public async runClientAreaProvisionFunctions(ignoreStored = false) {
    try {
      const functionsResults = await this.$store.dispatch(
        `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/runClientAreaProvisionFunctions`,
        {
          contractId: this.contractId,
          productId: this.productId,
          vm: this,
          ignoreStored
        }
      );

      if (!_.isEmpty(functionsResults))
        await Promise.all([
          this.getContractProductFields(true),
          this.getContractProductFieldsValues(true)
        ]);
    } catch (error) {
      // silently catch
    }
  }

  public updateContractProductFieldsValues(data) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/updateContractProductFieldsValues`,
      {
        contractId: this.contractId,
        productId: this.productId,
        data
      }
    );
  }

  public getContractProductFunctions(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/listContractProductFunctions`,
      {
        contractId: this.contractId,
        productId: this.productId,
        scope: this.contractProductFunctionsScope,
        vm: this,
        ignoreStored
      }
    );
  }

  public getContractProductProvisionRequests(limit, offset) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/listContractProductProvisionRequests`,
      {
        contractId: this.contractId,
        productId: this.productId,
        scope: this.contractProductProvisionRequestsScope,
        params: {
          order: "-created_at",
          limit,
          offset,
          with: "status,actor"
        },
        merge: true,
        vm: this,
        returnData: false,
        ignoreStored: true
      }
    );
  }

  public getContractProductProvisionRequest(uuid) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/getContractProductProvisionRequest`,
      {
        contractId: this.contractId,
        productId: this.productId,
        uuid,
        vm: this,
        returnData: true
      }
    );
  }

  public getProvisioningFunctionFields(executedFunction, ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/listContractProductFunctionFields`,
      {
        contractId: this.contractId,
        productId: this.productId,
        functionId: executedFunction.id,
        scope: "",
        vm: this,
        returnData: true,
        storeData: false,
        ignoreStored
      }
    );
  }

  public async openProductFunctionSetupModal(provisioningFunction, events) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/openProductFunctionSetupModal`,
      {
        props: {
          contractId: this.contractId,
          productId: this.productId,
          executedFunction: provisioningFunction
        },
        events
      }
    );
  }

  public async openDisplayFieldsModal(fields) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/openDisplayFieldsModal`,
      {
        props: { fields }
      }
    );
  }

  public async refreshProvisioningData(
    requestResults,
    functionId,
    refresh = true
  ) {
    if (this.isAdmin) {
      this.$bus.$emit("refreshRequestTable");
    }

    if (
      refresh ||
      requestResults.status.code === PRSC.PROVISION_REQUEST_ERROR
    ) {
      await Promise.all([
        this.getContractProductFunctions(true),
        this.getContractProductFieldsValues(true),
        this.$cProdMethods.loadContractData(true)
      ]);

      this.$bus.$emit("initialiseConfigurationForm");

      if (!_.isEmpty(requestResults)) {
        this.executeActions(requestResults);
      }

      this.stopFunctionExecution(functionId);
      return;
    }

    this.setFunctionTimeout(functionId, requestResults);
  }

  public manuallyResolveRequest(requestId: IProvisionRequest["id"]) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/updateRequest`,
      {
        requestId,
        data: {
          unresolved: false
        }
      }
    );
  }

  // Form
  public get cProdIsActive() {
    return (
      _.get(this.cProdData, "contractProduct.status.code") ===
      ContractStatusCodes.ACTIVE
    );
  }

  public get cProdIsStaged() {
    return this.cProdData?.isStaged;
  }

  // Functions
  public executedFunctions: IProvisionExecutedFunction[] = [];

  public get cProdProvFunctions() {
    return _.map(this.contractProductFunctions, func => {
      func.isDisabled = this.cProdIsStaged;
      return func;
    });
  }

  public async executeFunction(
    cProdProvFunction: IProvisionConfigurationFunction
  ) {
    if (cProdProvFunction.isDisabled) return;

    try {
      const fields = await this.getProvisioningFunctionFields(
        cProdProvFunction,
        true
      );

      if (!fields.length) {
        this.executedFunctions.push({
          id: cProdProvFunction.id,
          timeout: null,
          async: cProdProvFunction.async
        });

        // Execute function
        const request: IProvisionRequest = await this.executeProvisioningFunction(
          {
            provision_field_values: {},
            blueprint_function_id: cProdProvFunction.id
          }
        );

        // Handle result error and exit method
        if (request.status?.code === PRSC.PROVISION_REQUEST_ERROR) {
          const errorResult = _.find(request.results || [], r => !!r.error_id);
          this.stopFunctionExecution(cProdProvFunction.id);
          return this.$buefy.snackbar.open({
            message:
              errorResult?.message ||
              (this.$t("_sentence.something_went_wrong") as string),
            actionText: this.$t("_action.dismiss") as string,
            type: "is-danger",
            indefinite: true
          });
        } else {
          const message = _.get(request, "result_message");
          if (message) this.$store.dispatch("toast/show", { message: message });
        }

        // Refresh provisioning data
        this.refreshProvisioningData(
          request,
          cProdProvFunction.id,
          !cProdProvFunction.async
        );
        return;
      }

      this.openProductFunctionSetupModal(cProdProvFunction, {
        success: results => {
          this.executedFunctions.push({
            id: cProdProvFunction.id,
            timeout: null,
            async: cProdProvFunction.async
          });

          this.refreshProvisioningData(
            results,
            cProdProvFunction.id,
            !cProdProvFunction.async
          );
        }
      });
    } catch (error) {
      this.stopFunctionExecution(cProdProvFunction.id);
      this.$store.dispatch("api/handleError", error);
    }
  }

  public executeActions(requestResults) {
    _.forEach(_.get(requestResults, "action_logs"), actionLog => {
      if (!actionLog.success) return;

      if (actionLog.action.type === "redirect") {
        const href = _.get(actionLog, "data.redirect_url");
        const spawned = window.open(href, "_blank");
        if (!spawned || typeof spawned.closed === "undefined") {
          this.$store.dispatch("ui/openAllowPopupsModal", () =>
            location.assign(href)
          );
        }
      }

      if (actionLog.action.type === "display_return_fields") {
        const fields = _.get(actionLog, "data.display_fields");

        if (fields.length) {
          this.openDisplayFieldsModal(fields);
        }
      }
    });
  }

  public executeProvisioningFunction(data: IProvisioningFunctionSetup) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONTRACT_PRODUCTS}/executeProvisioningFunction`,
      {
        contractId: this.contractId,
        productId: this.productId,
        data
      }
    );
  }

  public setAsyncFunctionExecutedEvent() {
    this.$bus.$on(
      "socket-provision-result-received",
      async (socketMessage: ISocketMessage) => {
        const data = JSON.parse(socketMessage.message);
        try {
          const request: any = await this.getContractProductProvisionRequest(
            data.request_uuid
          );

          this.$store.dispatch("toast/show", {
            message: _.get(request, "results[0].message")
          });

          this.refreshProvisioningData(request, request.blueprint_function_id);
        } catch (error) {
          this.$store.dispatch("api/handleError", error);
        }
      }
    );
  }

  public setFunctionTimeout(functionId, requestResults) {
    this.executedFunctions[
      _.findIndex(this.executedFunctions, ["id", functionId])
    ].timeout = setTimeout(() => {
      this.refreshProvisioningData(requestResults, functionId);
    }, 120000);
  }

  public stopFunctionExecution(
    functionId: IProvisionConfigurationFunction["id"]
  ) {
    const index = _.findIndex(this.executedFunctions, ["id", functionId]);

    if (index === -1) return;

    clearTimeout(this.executedFunctions[index].timeout);

    this.executedFunctions = _.filter(
      this.executedFunctions,
      executedFunction => executedFunction.id !== functionId
    );
  }

  // Provide

  @Provide("cProdProvProviderMethods")
  public get providerMethods(): ICProdProvProviderMethods {
    return {
      executeFunction: this.executeFunction,
      getContractProductFieldsValues: this.getContractProductFieldsValues,
      reloadData: this.reloadData,
      updateContractProductFieldsValues: this.updateContractProductFieldsValues,
      manuallyResolveRequest: this.manuallyResolveRequest
    };
  }

  @Provide("cProdProvProvider")
  public providerData(): ICProdProvProvider {
    return {
      canManageProvisioning: this.canManageProvisioning,
      contractId: this.contractId,
      contractProductFields: this.contractProductFields,
      contractProductFieldsValues: this.contractProductFieldsValues,
      cProdIsActive: this.cProdIsActive,
      cProdIsStaged: this.cProdIsStaged,
      cProdSetupFieldsConfirmed: this.cProdData?.contractProduct
        ?.provision_setup_fields_confirmed,
      cProdProvFunctions: this.cProdProvFunctions,
      contractProductProvisionRequests: this.contractProductProvisionRequests,
      executedFunctions: this.executedFunctions,
      hasProvisioning: this.hasProvisioning,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      productId: this.productId
    };
  }

  //  Render
  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isLoading && this.hasRequestError },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $cProdProvData: this.providerData(),
        $cProdProvMethods: this.providerMethods
      })
    );
  }
}
